import React from 'react';
import {observer} from 'mobx-react';
import autobind from 'common/decorators/autobind.js';
import {DirectoryStoreContextV2} from 'core/stores/RootStore.js';
import {
    FILTER_LANGUAGE,
    FILTER_GENDER,
    FILTER_TELEHEALTH,
    HEADER_SECTION,
    OFFERS_TELEHEALTH_FILTER,
    TOOLTIP_TELEHEALTH_TEXT
} from 'core/constants.js';
import {browserCheckForIE11} from 'core/utils.js';

import {DropdownV2,ToggleV2} from '@HealthShareAU/hs-component-library';

export default
@observer
class DirectoryHeaderV2 extends React.Component {
    static contextType = DirectoryStoreContextV2;

    constructor(props) {
        super(props);

        this.scrollContainerRef = React.createRef();
    }

    componentDidMount() {
        const isTelDirectory =
            this.props?.offersTelehealth && this.props.telehealthDirectory;

        // this searched sepcialty offer tel also in tel dir
        if (isTelDirectory) {
            this.context?.updateStore({
                selectedConsultationType: this.context?.consultationTypes[1],
            });
        } else {
            this.context?.updateStore({
                selectedConsultationType: this.context?.consultationTypes[0],
            });
        }
    }

    getHeading() {
        const {
            locality,
            specialty,
            interest,
            state,
            telehealthDirectory,
            rootStore: {
                paramStore: {isReferrals, isProUser, isClient},
            },
        } = this.context;
        let title =
            isReferrals || isProUser
                ? specialty.pluralProfessionalName
                : specialty.pluralName;
        if (isClient) {
            return null;
        }
        if (interest?.name) {
            title += ` with special interest "${interest.name}"`;
        }
        if (telehealthDirectory && state) {
            title += ` in ${state.name}`;
        } else if (locality) {
            title += ` in ${locality?.name} ${locality?.state?.name} ${locality?.postCode}`;
        }
        return title;
    }

    @autobind
    toggleTelehealthDirectory(event) {
        /*
         *   Triggers a redirect to the relevant directory page
         *     - checked: telehealth directory (telehealth-specialty in state)
         *     - unchecked: regular directory  (specialty in locality)
         */
        if (event && event.key === ' ') {
            // For space bar, prevent jumping to end of page
            event.preventDefault();
        }
        this.context.locationSearchAPI.updateStore({
            isLoading: true,
            results: [],
        });
        this.context.updateStore({
            bannerAdsHaveLoaded: false,
            telehealthDirectory: !this.context.telehealthDirectory,
            selectedConsultationType: this.context?.consultationTypes[1],
        });
        window.location.assign(this.context.toggleDirectoryURL);
    }

    scrollingHandler(e) {
        const tracker = this.scrollContainerRef.current;

        const selectedEleToLeft = e.target.getBoundingClientRect().left;
        const selectedEleToRight = e.target.getBoundingClientRect().right;
        const isCutOff =
            selectedEleToLeft < tracker.getBoundingClientRect().left ||
            selectedEleToRight > tracker.getBoundingClientRect().right;

        if (isCutOff) {
            // if the element is cut off then trigger the scrolling
            if (tracker) {
                tracker.scrollTo({
                    left: selectedEleToLeft,
                    behavior: 'smooth',
                });
            }
        }
    }

    @autobind
    filterTelehealthDirectoryV2(isTelDir, consultation) {
        if (isTelDir) {
            this.toggleTelehealthDirectory();
        } else {
            this.context.updateStore({
                filterByTelehealth: !this.context.filterByTelehealth,
                selectedConsultationType: consultation,
            });
        }
    }

    @autobind
    filterTelehealthDirectory(e, emphasized) {
        this.scrollingHandler(e);
        if (emphasized) {
            this.toggleTelehealthDirectory(e);
        } else {
            this.context.updateStore({
                filterByTelehealth: !this.context.filterByTelehealth,
            });
        }
    }

    @autobind
    renderFilters() {
        const {
            genders,
            languages,
            consultationTypes,
            selectedLanguage,
            selectedGender,
            selectedConsultationType,
            rootStore: {
                healthFundStore: {healthFundThemeColor},
                paramStore: {hasSkin},
            },
        } = this.context;
        const skinned =
            healthFundThemeColor && hasSkin ? healthFundThemeColor : null;

        const handleSelectFilter = (
            name,
            filterBy,
            filterOptions,
            selectedOptionId,
        ) => {
            const option = filterOptions.find(
                (opt) => opt.id === selectedOptionId,
            );
            this.context.updateStore({
                bannerAdsIsLoading: true,
                [filterBy]: option || {
                    id: 'any',
                    name: `Any ${name}`,
                },
            });
        };

        const renderNewF2fFilter = () => {
            return (
                <DropdownV2
                    asFilter
                    customClass="tel-filter"
                    colorTheme={skinned}
                    dataTestId={OFFERS_TELEHEALTH_FILTER}
                    gaObjectType={HEADER_SECTION}
                    gaSelector={OFFERS_TELEHEALTH_FILTER}
                    onChange={(consultation) => {
                        this.filterTelehealthDirectoryV2(
                            this.props?.offersTelehealth,
                            consultation,
                        );
                    }}
                    options={consultationTypes}
                    value={selectedConsultationType}
                    onClick={(e) => {
                        this.scrollingHandler(e);
                    }}
                    tooltip="Filter practitioners by consultation type — face-to-face or telehealth."
                />
            );
        };

        const renderTelOffer = () => {
            return (
                <ToggleV2
                    colorTheme={skinned}
                    checked={
                        this.props?.offersTelehealth
                            ? this.props.telehealthDirectory
                            : this.context?.filterByTelehealth
                    }
                    dataTestId={FILTER_TELEHEALTH}
                    gaSelector={FILTER_TELEHEALTH}
                    gaObjectType={HEADER_SECTION}
                    icon={'phonePlus'}
                    onClick={(e) => {
                        this.filterTelehealthDirectory(
                            e,
                            this.props?.offersTelehealth,
                        );
                    }}
                    text={'Offers Telehealth'}
                    tooltip={
                        browserCheckForIE11() ? null : TOOLTIP_TELEHEALTH_TEXT
                    }
                />
            );
        };

        return (
            <>
                {this.context.enableDirectoryF2fFilter
                    ? renderNewF2fFilter()
                    : renderTelOffer()}
                <DropdownV2
                    asFilter
                    colorTheme={skinned}
                    dataTestId={FILTER_LANGUAGE}
                    gaObjectType={HEADER_SECTION}
                    gaSelector={FILTER_LANGUAGE}
                    onChange={(languageOption) => {
                        handleSelectFilter(
                            'Language',
                            'selectedLanguage',
                            languages,
                            languageOption.id,
                        );
                    }}
                    options={languages}
                    value={selectedLanguage}
                    onClick={(e) => {
                        this.scrollingHandler(e);
                    }}
                />
                <DropdownV2
                    asFilter
                    colorTheme={skinned}
                    dataTestId={FILTER_GENDER}
                    gaObjectType={HEADER_SECTION}
                    gaSelector={FILTER_GENDER}
                    onChange={(genderOption) => {
                        handleSelectFilter(
                            'Gender',
                            'selectedGender',
                            genders,
                            genderOption.id,
                        );
                    }}
                    options={genders}
                    value={selectedGender}
                    onClick={(e) => {
                        this.scrollingHandler(e);
                    }}
                />
            </>
        );
    }

    render() {
        const {
            rootStore: {
                healthFundStore: {healthFundsLoading},
                paramStore: {isClient},
            },
        } = this.context;
        return (
            <header className="directory-header">
                {!isClient && <h1>{this.getHeading()}</h1>}
                <div className="filter-container">
                    <section className="filters">
                        <div className="tracker" ref={this.scrollContainerRef}>
                            {!healthFundsLoading && this.renderFilters()}
                        </div>
                    </section>
                </div>
            </header>
        );
    }
}
